<template>
    <v-card class="revenue-forecast elevation-2">
        <v-card-title class="primary--text">
            <v-icon left color="primary">mdi-chart-line</v-icon>
            Predpokladané ročné tržby
        </v-card-title>

        <v-card-text>
            <!-- Ovládacie prvky -->
            <v-row class="mb-4">
                <!-- <v-col cols="12" md="3">
                    <v-text-field v-model.number="customAnnualRevenue" label="Modelovanie ročných tržieb (€)"
                        type="number" outlined dense @input="updateRevenue">
                        <template v-slot:append>
                            <v-tooltip top>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon v-bind="attrs" v-on="on">mdi-information</v-icon>
                                </template>
                                <span>Upravte predpokladané ročné tržby pre modelovanie výnosov</span>
                            </v-tooltip>
                        </template>
                    </v-text-field>
                </v-col> -->
                <v-col cols="12" md="2">
                    <v-select v-model="selectedYear" :items="availableYears" label="Zobrazený rok" outlined dense
                        @change="updateSelectedYear"></v-select>
                </v-col>
                <v-col cols="12" md="2">
                    <v-slider v-model="payoutPercentage" label="Koeficient platby z tržieb" min="60" max="70" step="1"
                        thumb-label :thumb-size="24" class="mt-2" @input="calculateTotals" />
                </v-col>
                <v-col cols="12" md="2">
                    <v-slider v-model="penetrationPercentage" label="Naplnenie plánov" min="10" max="1000" step="5"
                        thumb-label :thumb-size="24" class="mt-2" @input="calculateTotals" />
                </v-col>
                <v-col cols="12" md="3">
                    <v-text-field v-model.number="pricePerMonth" label="Cena/mesiac pre užívateľa (€)" type="number"
                        outlined dense @input="calculateTotals" />
                </v-col>
            </v-row>

            <!-- Nastavenia počtu používateľov podľa regiónov -->
            <v-row class="mb-4">
                <v-col cols="12">
                    <v-card outlined>
                        <v-card-subtitle>
                            <v-icon left small>mdi-account-group</v-icon>
                            Počet používateľov podľa regiónov (pre rok {{ selectedYear }})
                        </v-card-subtitle>
                        <v-card-text>
                            <v-row>
                                <v-col cols="12" md="4">
                                    <v-text-field v-model.number="userCounts.slovakia" label="Slovensko" type="number"
                                        outlined dense @input="calculateTotals">
                                        <template v-slot:prepend-inner>
                                            <v-icon color="primary">mdi-flag</v-icon>
                                        </template>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-text-field v-model.number="userCounts.europe" label="Európa (mimo SK)"
                                        type="number" outlined dense @input="calculateTotals">
                                        <template v-slot:prepend-inner>
                                            <v-icon color="info">mdi-earth</v-icon>
                                        </template>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-text-field v-model.number="userCounts.global" label="Globálne (mimo EU)"
                                        type="number" outlined dense @input="calculateTotals">
                                        <template v-slot:prepend-inner>
                                            <v-icon color="success">mdi-web</v-icon>
                                        </template>
                                    </v-text-field>
                                </v-col>
                            </v-row>

                            <v-row>
                                <v-col cols="12" md="6">
                                    <div class="text-center">
                                        <div class="text-caption">Celkový počet používateľov</div>
                                        <div class="text-h5 primary--text">{{ totalUsers.toLocaleString('sk-SK') }}
                                        </div>
                                    </div>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <div class="text-center">
                                        <div class="text-caption">Priemerný príjem na používateľa/rok</div>
                                        <div class="text-h5 info--text">{{ formatCurrency(averageRevenuePerUser) }}
                                        </div>
                                    </div>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Súhrnné informácie -->
            <v-row class="mb-4">
                <v-col cols="12">
                    <v-card outlined color="info lighten-5">
                        <v-card-text>
                            <v-row>
                                <v-col cols="12" sm="6" md="3">
                                    <div class="text-center">
                                        <div class="text-caption">Celkové tržby (rok {{ selectedYear }})</div>
                                        <div class="text-h5 info--text">{{ formatCurrency(totalRevenue) }}</div>
                                    </div>
                                </v-col>
                                <v-col cols="12" sm="6" md="3">
                                    <div class="text-center">
                                        <div class="text-caption">Percento na výplaty</div>
                                        <div class="text-h5 warning--text">{{ payoutPercentage }}%</div>
                                    </div>
                                </v-col>
                                <v-col cols="12" sm="6" md="3">
                                    <div class="text-center">
                                        <div class="text-caption">Suma na výplaty investorom</div>
                                        <div class="text-h5 success--text">{{ formatCurrency(totalPayoutAmount) }}</div>
                                    </div>
                                </v-col>
                                <v-col cols="12" sm="6" md="3">
                                    <div class="text-center">
                                        <div class="text-caption">Zostáva spoločnosti</div>
                                        <div class="text-h5 primary--text">{{ formatCurrency(companyRevenue) }}</div>
                                    </div>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Tabuľka projektov -->
            <v-row>
                <v-col cols="12">
                    <v-card outlined>
                        <v-card-subtitle>
                            Predpokladané tržby jednotlivých projektov
                        </v-card-subtitle>
                        <v-card-text>
                            <v-data-table :headers="projectHeaders" :items="projectsWithCalculations" :loading="loading"
                                class="elevation-1" hide-default-footer :items-per-page="-1">
                                <template v-slot:item.name="{ item }">
                                    <div class="d-flex align-center">
                                        <v-icon left small>
                                            {{ item.category }}
                                        </v-icon>
                                        <strong>{{ item.name }}</strong>
                                    </div>
                                </template>

                                <template v-slot:item.usersSlovakia="{ item }">
                                    <strong class="primary--text">{{ item.usersSlovakia.toLocaleString('sk-SK')
                                    }}</strong>
                                </template>

                                <template v-slot:item.usersEurope="{ item }">
                                    <strong class="info--text">{{ item.usersEurope.toLocaleString('sk-SK') }}</strong>
                                </template>

                                <template v-slot:item.usersGlobal="{ item }">
                                    <strong class="success--text">{{ item.usersGlobal.toLocaleString('sk-SK')
                                    }}</strong>
                                </template>

                                <template v-slot:item.currentYearRevenue="{ item }">
                                    <strong class="info--text">{{ formatCurrency(item.currentYearRevenue) }}</strong>
                                </template>

                                <template v-slot:item.payoutAmount="{ item }">
                                    <strong class="success--text">{{ formatCurrency(item.payoutAmount) }}</strong>
                                </template>

                                <template v-slot:item.growth="{ item }">
                                    <v-chip small :color="getGrowthColor(item.growth)"
                                        :text-color="item.growth >= 0 ? 'white' : 'black'">
                                        <v-icon left small>
                                            {{ item.growth >= 0 ? 'mdi-trending-up' : 'mdi-trending-down' }}
                                        </v-icon>
                                        {{ item.growth >= 0 ? '+' : '' }}{{ item.growth.toFixed(1) }}%
                                    </v-chip>
                                </template>

                                <!-- Footer row with totals -->
                                <template v-slot:body.append>
                                    <tr class="font-weight-bold grey lighten-4">
                                        <td colspan="2"><strong>CELKOM</strong></td>
                                        <td class="text-right"><strong>{{ totalUsers.toLocaleString('sk-SK') }}</strong>
                                        </td>
                                        <td colspan="2"></td>
                                        <td class="text-right"><strong class="info--text">{{
                                            formatCurrency(totalRevenue) }}</strong></td>
                                        <td class="text-right"><strong class="success--text">{{
                                            formatCurrency(totalPayoutAmount) }}</strong></td>
                                        <td>-</td>
                                    </tr>
                                </template>
                            </v-data-table>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Grafické zobrazenie -->
            <v-row class="mt-4">
                <!-- <v-col cols="12" md="6">
                    <v-card outlined>
                        <v-card-title class="text-h6">Vývoj tržieb v čase</v-card-title>
                        <v-card-text>
                            <div class="chart-container">
                                <canvas ref="revenueChart" height="200"></canvas>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col> -->
                <v-col cols="12" md="12">
                    <v-card outlined>
                        <v-card-title class="text-h6">Rozdelenie na investorov vs spoločnosť</v-card-title>
                        <v-card-text>
                            <div class="text-center">
                                <v-progress-circular :value="(totalPayoutAmount / totalRevenue) * 100" :size="150"
                                    :width="15" color="success">
                                    <span class="text-h6">{{ payoutPercentage }}%</span>
                                </v-progress-circular>
                                <div class="mt-4">
                                    <div class="d-flex justify-space-between align-center mb-2">
                                        <span class="success--text">Investorom:</span>
                                        <strong class="success--text">{{ formatCurrency(totalPayoutAmount) }}</strong>
                                    </div>
                                    <div class="d-flex justify-space-between align-center">
                                        <span class="primary--text">Spoločnosti:</span>
                                        <strong class="primary--text">{{ formatCurrency(companyRevenue) }}</strong>
                                    </div>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Reálne tržby (ak sú dostupné) -->
            <v-row class="mt-4" v-if="realRevenues.length > 0">
                <v-col cols="12">
                    <v-card outlined>
                        <v-card-subtitle>
                            <v-icon left small>mdi-chart-bar</v-icon>
                            Reálne tržby vs. Predpoklad
                        </v-card-subtitle>
                        <v-card-text>
                            <v-simple-table>
                                <template v-slot:default>
                                    <thead>
                                        <tr>
                                            <th>Obdobie</th>
                                            <th>Reálne tržby</th>
                                            <th>Predpoklad</th>
                                            <th>Rozdiel</th>
                                            <th>% na investorov</th>
                                            <th>Suma investorom</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="revenue in realRevenues" :key="revenue.period">
                                            <td>{{ revenue.period }}</td>
                                            <td><strong>{{ formatCurrency(revenue.actual) }}</strong></td>
                                            <td>{{ formatCurrency(revenue.forecast) }}</td>
                                            <td :class="revenue.difference >= 0 ? 'success--text' : 'error--text'">
                                                {{ formatCurrency(revenue.difference) }}
                                            </td>
                                            <td>{{ revenue.payoutPercentage }}%</td>
                                            <td class="success--text">
                                                <strong>{{ formatCurrency(revenue.payoutAmount) }}</strong>
                                            </td>
                                        </tr>
                                    </tbody>
                                </template>
                            </v-simple-table>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Poznámky a nastavenia -->
            <v-row class="mt-4">
                <v-col cols="12">
                    <v-expansion-panels flat>
                        <v-expansion-panel>
                            <v-expansion-panel-header>
                                <div class="d-flex align-center">
                                    <v-icon left color="info">mdi-information</v-icon>
                                    <span class="font-weight-medium">Poznámky k modelovaniu</span>
                                </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <v-row>
                                    <v-col cols="12" md="6">
                                        <div class="text-subtitle-2 mb-2">📊 Modelovanie parametrov:</div>
                                        <ul class="text-body-2 ml-4">
                                            <li><strong>Naplnenie plánov:</strong> {{ penetrationPercentage }}% z
                                                plánovaného počtu
                                                užívateľov</li>
                                            <li><strong>Cena/mesiac:</strong> Približná priemerná cena každého projektu
                                                za 1 mesiac
                                                pre užívateľa</li>
                                            <li><strong>Penetrácia:</strong> Postupné rozbiehanie projektov medzi
                                                užívateľskou
                                                verejnosťou</li>
                                            <li><strong>Koeficient:</strong> Možnosť meniť od 60% do 70%</li>
                                        </ul>
                                    </v-col>
                                    <v-col cols="12" md="6">
                                        <div class="text-subtitle-2 mb-2">⚠️ Dôležité upozornenia:</div>
                                        <ul class="text-body-2 ml-4">
                                            <li>Výsledky sú len <strong>informatívne</strong> a nie sú právne záväzné
                                            </li>
                                            <li>Môžete modelovať až do <strong>1000%</strong> naplnenia plánov</li>
                                            <li>Tržby závisia od reálneho počtu užívateľov a ich aktivity</li>
                                            <li>Výplaty sú závislé od skutočných dosiahnutých tržieb</li>
                                        </ul>
                                    </v-col>
                                </v-row>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </v-col>
            </v-row>
        </v-card-text>
    </v-card>
</template>

<script>
export default {
    name: "RevenueForecastTable",

    props: {
        projects: {
            type: Array,
            default: () => [
            ]
        },
        annualRevenue: {
            type: Number,
            default: 3420000 // Z Excel analýzy - prvý rok
        },
        realRevenues: {
            type: Array,
            default: () => [
            ]
        },
    },

    data() {
        return {
            loading: false,
            customAnnualRevenue: this.annualRevenue,
            selectedYear: 1,
            payoutPercentage: 60, // Prednastavené 60%, možnosť 60-70%
            penetrationPercentage: 15, // Z Excel analýzy - 15% naplnenie v prvom roku
            pricePerMonth: 100, // Z Excel analýzy - približná cena/mesiac

            // Počty používateľov podľa regiónov (z Excel analýzy)
            userCounts: {
                slovakia: 100000,    // C18 v Excel - Slovensko
                europe: 300000,     // C19 v Excel - Európa
                global: 500000      // C20 v Excel - Globálne
            },

            availableYears: [
                { text: '1. rok', value: 1 },
                { text: '2. rok', value: 2 },
                { text: '3. rok', value: 3 }
            ],

            projectHeaders: [
                { text: 'Projekt č.', value: 'id', width: '100px' },
                { text: 'Názov projektu', value: 'name' },
                { text: 'Používatelia SK', value: 'usersSlovakia', align: 'right' },
                { text: 'Používatelia EU', value: 'usersEurope', align: 'right' },
                { text: 'Používatelia Global', value: 'usersGlobal', align: 'right' },
                { text: 'Ročná tržba', value: 'currentYearRevenue', align: 'right' },
                { text: 'Na investorov', value: 'payoutAmount', align: 'right' },
            ],

            // Sample real revenue data z Excel tabuľky "Reálne tržby"
            // realRevenues: [
            //     {
            //         period: 'Január 2025',
            //         actual: 285362,
            //         forecast: 285000,
            //         difference: 362,
            //         payoutPercentage: 60,
            //         payoutAmount: 171217
            //     },
            //     {
            //         period: 'Február 2025',
            //         actual: 285362,
            //         forecast: 290000,
            //         difference: -4638,
            //         payoutPercentage: 70,
            //         payoutAmount: 199753
            //     }
            // ]
        };
    },

    computed: {
        projectsWithCalculations() {
            return this.projects.map(project => {
                // Rozdelenie používateľov podľa projektu (každý projekt má rovnaký podiel)
                const usersPerProject = {
                    slovakia: Math.round(this.userCounts.slovakia / this.projects.length),
                    europe: Math.round(this.userCounts.europe / this.projects.length),
                    global: Math.round(this.userCounts.global / this.projects.length)
                };

                // Výpočet tržby na základe počtu používateľov a ceny
                const monthlyRevenue = (usersPerProject.slovakia + usersPerProject.europe + usersPerProject.global) * this.pricePerMonth;
                const annualRevenue = monthlyRevenue * 12;

                // Aplikácia penetrácie a ročného koeficientu
                const penetrationFactor = this.penetrationPercentage / 100;
                const yearMultiplier = this.getYearMultiplier();

                const currentYearRevenue = annualRevenue * penetrationFactor * yearMultiplier;
                const payoutAmount = currentYearRevenue * (this.payoutPercentage / 100);
                const growth = this.calculateGrowth(project);

                return {
                    ...project,
                    usersSlovakia: usersPerProject.slovakia,
                    usersEurope: usersPerProject.europe,
                    usersGlobal: usersPerProject.global,
                    currentYearRevenue,
                    payoutAmount,
                };
            });
        },

        totalRevenue() {
            return this.projectsWithCalculations.reduce((sum, project) => {
                return sum + project.currentYearRevenue;
            }, 0);
        },

        totalPayoutAmount() {
            return this.totalRevenue * (this.payoutPercentage / 100);
        },

        companyRevenue() {
            return this.totalRevenue - this.totalPayoutAmount;
        },

        totalUsers() {
            return this.userCounts.slovakia + this.userCounts.europe + this.userCounts.global;
        },

        averageRevenuePerUser() {
            return this.totalUsers > 0 ? (this.totalRevenue / this.totalUsers) : 0;
        }
    },

    watch: {
        annualRevenue(newVal) {
            this.customAnnualRevenue = newVal;
        }
    },

    mounted() {
        this.calculateTotals();
    },

    methods: {
        getCurrentYearRevenue(project) {
            switch (this.selectedYear) {
                case 1: return project.year1 || 0;
                case 2: return project.year2 || 0;
                case 3: return project.year3 || 0;
                default: return project.year1 || 0;
            }
        },

        getYearMultiplier() {
            // Multiplikátor pre rôzne roky (1. rok = 1x, 2. rok = 3x, 3. rok = 5x)
            switch (this.selectedYear) {
                case 1: return 1;
                case 2: return 3;
                case 3: return 5;
                default: return 1;
            }
        },

        calculateGrowth(project) {
            if (this.selectedYear === 1) return 0;

            const currentMultiplier = this.getYearMultiplier();
            const previousMultiplier = this.selectedYear === 2 ? 1 : 3;

            return ((currentMultiplier - previousMultiplier) / previousMultiplier) * 100;
        },

        getTotalForYear(year) {
            const yearMultiplier = year === 1 ? 1 : year === 2 ? 3 : 5;
            const baseRevenue = this.totalUsers * this.pricePerMonth * 12;
            return baseRevenue * (this.penetrationPercentage / 100) * yearMultiplier;
        },

        getGrowthColor(growth) {
            if (growth > 100) return 'success';
            if (growth > 0) return 'info';
            if (growth > -10) return 'warning';
            return 'error';
        },

        calculateTotals() {
            // Trigger reactive recalculation
            this.$forceUpdate();
        },

        updateRevenue() {
            // Prepočítaj na základe používateľov a ceny
            const newRevenue = this.totalUsers * this.pricePerMonth * 12 * (this.penetrationPercentage / 100) * this.getYearMultiplier();
            this.customAnnualRevenue = newRevenue;
            this.$emit('revenue-change', this.customAnnualRevenue);
            this.calculateTotals();
        },

        updateSelectedYear() {
            this.updateRevenue();
        },

        formatCurrency(amount) {
            // Handle null, undefined, empty string
            if (amount == null || amount === '') return '0€';

            // Convert string to number (handles Django DecimalField)
            const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

            // Check if conversion resulted in a valid number
            if (isNaN(numAmount)) return '0€';

            return new Intl.NumberFormat('sk-SK', {
                style: 'currency',
                currency: 'EUR'
            }).format(numAmount);
        }
    }
};
</script>

<style scoped>
.revenue-forecast {
    margin-bottom: 24px;
}

.v-data-table>>>.v-data-table__wrapper {
    border-radius: 8px;
}

.font-weight-bold {
    font-weight: 600 !important;
}

.v-text-field>>>.v-input__control {
    min-height: 32px;
}

.v-text-field>>>.v-text-field__details {
    display: none;
}

.chart-container {
    height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f5f5f5;
    border-radius: 8px;
}

.chart-container::before {
    content: 'Graf tržieb (Chart.js implementácia)';
    color: #666;
    font-style: italic;
}

@media (max-width: 600px) {
    .text-h5 {
        font-size: 1.3rem !important;
    }

    .text-h6 {
        font-size: 1.1rem !important;
    }
}
</style>